import type { Match } from '@uefadigital/panenka';
import { LineupStatus, MatchStatus } from '@uefadigital/panenka';
import { PkButton, PkIcon, PkMatchHeader, PkTab, PkTabs } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useGetMatchDetailsSectionsQuery } from 'api/cmsApi';
import { Matches } from 'components/Matches';
import useIsDesktop from 'hooks/useIsDesktop';
import { useMatchUnitTranslations } from 'hooks/useMatchUnitTranslations';
import { routes } from 'routes';
import { publicPath } from 'signals/publicPath';
import type { ConfigCompetition } from 'utils/config';

import * as style from './style.module.css';

export interface MatchProps {
  matchData: Match;
  competitionConfig: ConfigCompetition;
  seasonYear: string;
  matchId: string;
}

export const MatchHeader = ({ matchData, competitionConfig, seasonYear, matchId }: MatchProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const muTranslations = useMatchUnitTranslations();

  const PUBLIC_PATH = publicPath.value;

  const matchHeaderBgName = isDesktop ? competitionConfig.matchHeader : competitionConfig.matchHeaderMobile;
  const backgroundImage = matchHeaderBgName
    ? {
        backgroundImage: `url('${import.meta.env.BASE_URL}assets/matchHeaders/${matchHeaderBgName}')`,
        backgroundSize: 'cover',
      }
    : {};

  const matchPath = `${PUBLIC_PATH}competitions/${competitionConfig.name}/${seasonYear}/match/${matchId}`;
  const pathName = window.location.pathname;

  const { data: visibilityOptions } = useGetMatchDetailsSectionsQuery({
    matchId,
    competitions: competitionConfig.id,
    language: i18n.language,
  });

  const tabsConfig = [
    {
      name: t('match_overview'),
      path: matchPath,
      isAvailable: true,
    },
    {
      name: t('squad_details'),
      path: `${matchPath}/${routes.squadDetails}`,
      isAvailable: !visibilityOptions?.disableSquadList,
    },
    {
      name: t('match_officials'),
      path: `${matchPath}/${routes.officials}`,
      isAvailable: matchData?.referees && matchData.referees.length > 0,
    },
    {
      name: t('line_ups'),
      path: `${matchPath}/${routes.lineUps}`,
      isAvailable: true,
    },
    {
      name: t('statistics'),
      path: `${matchPath}/${routes.statistics}`,
      isAvailable: matchData.status !== MatchStatus.UPCOMING,
    },
    {
      name: t('standings'),
      path: `${matchPath}/${routes.standings}`,
      isAvailable: true,
    },
    {
      name: t('print_match_information_kits'),
      path: `${matchPath}/${routes.printMatchPressKits}`,
      isAvailable: matchData.lineupStatus !== LineupStatus.NOT_AVAILABLE,
    },
  ];

  const tabsAvailable = tabsConfig.filter((tab) => {
    return tab.isAvailable;
  });

  const handleGoBack = () => {
    navigate(`${PUBLIC_PATH}competitions/${competitionConfig.name}/${seasonYear}`);
  };

  return (
    <div style={backgroundImage} className="pk-bg--elevation-01">
      <div className="pk-container pk-pt--xs">
        <div
          className={
            'pk-col pk-col--span-12 pk-d--flex pk-flex--column pk-flex-sm--row pk-gap--xs pk-align-items--start pk-align-items-sm--center pk-justify-content-sm--between'
          }
        >
          <PkButton
            className={style.backLink}
            onPkClick={handleGoBack}
            width="auto"
            appearance="outlined"
            high-contrast
          >
            <PkIcon name="navigation-chevron-left" color="text-02" size={16} />
            {t('back')}
          </PkButton>

          <div className="pk-d--flex pk-w--100 pk-w-sm--auto pk-gap--s">
            <Matches matchData={matchData} />
            <PkButton
              onPkClick={() => window.print()}
              className={clsx('pk-d--none pk-d-sm--block', style.printButton)}
              width="auto"
              appearance="outlined"
              high-contrast
            >
              {t('print_information_kit')}
              <PkIcon name="actions-print" size={20} className="pk-ml--xs" />
            </PkButton>
          </div>
        </div>
        <div className="pk-col pk-col--span-12">
          <PkMatchHeader matchData={matchData} {...muTranslations} />
        </div>
      </div>
      <div className="pk-container pk-py--0">
        <div className="pk-col pk-col--span-12">
          <PkTabs className="pk-mt--xl2">
            {tabsAvailable.map((tab, index) => {
              return (
                <PkTab
                  key={index}
                  selected={tab.path === pathName || `${tab.path}/` === pathName}
                  tab-id={index}
                  onPkTabButtonClick={() => navigate(tab.path)}
                >
                  <span className={`${isDesktop ? 'pk-font-size--m' : 'pk-font-size--s pk-line-height--md'}`}>
                    {tab.name}
                  </span>
                </PkTab>
              );
            })}
          </PkTabs>
        </div>
      </div>
    </div>
  );
};
